import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError, API } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Plus, Upload, Eye, Pencil, Trash2, FileText, AlertTriangle, X, Download } from "lucide-react";
import { toast } from "sonner";
import PdfViewer from "@/components/PdfViewer";
import FileUploader from "@/components/FileUploader";
import AuthImage from "@/components/AuthImage";
import ContextualHelpLink from "@/components/ContextualHelpLink";
import { usePickLists } from "@/hooks/usePickLists";

const HOLDER_TYPES = { contractor: "Entreprise", tenant: "Locataire", owner: "Propriétaire", other: "Autre" };
const COVERAGE_TYPES = { rc_pro: "RC Pro", decennale: "Décennale", multirisque: "Multi-risque", dommages_ouvrage: "Dommages-ouvrage", other: "Autre" };
const STATUS = {
  valid: { label: "Valide", cls: "bg-emerald-100 text-emerald-700" },
  expiring_soon: { label: "Expire bientôt", cls: "bg-amber-100 text-amber-700" },
  expired: { label: "Expiré", cls: "bg-red-100 text-red-700" },
  unknown: { label: "?", cls: "bg-slate-100 text-slate-600" },
};

const emptyForm = {
  holder_name: "", holder_type: "contractor",
  asset_id: "", contractor_id: "", policy_id: "",
  insurer_name: "", policy_number: "",
  coverage_type: "rc_pro", coverage_amount: "",
  start_date: "", end_date: "",
  storage_path: "", notes: "",
};

export default function Insurance() {
  const qc = useQueryClient();
  const pl = usePickLists();
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilters, setScopeFilters] = useState({ client_id: "all", legal_entity_id: "all", asset_id: "all" });
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [viewing, setViewing] = useState(null);

  const { data: certs = [] } = useQuery({ queryKey: ["certificates"], queryFn: () => api.get("/insurance-certificates").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: policies = [] } = useQuery({ queryKey: ["policies"], queryFn: () => api.get("/policies").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: legalEntities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });

  const assetById = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);
  const policyById = useMemo(() => Object.fromEntries(policies.map((p) => [p.id, p])), [policies]);
  const policiesForAsset = useMemo(() => {
    if (!form.asset_id) return policies;
    return policies.filter((p) => !p.asset_id || p.asset_id === form.asset_id);
  }, [policies, form.asset_id]);

  const legalEntitiesForClient = useMemo(() => (
    scopeFilters.client_id === "all" ? legalEntities : legalEntities.filter((le) => le.client_id === scopeFilters.client_id)
  ), [legalEntities, scopeFilters.client_id]);
  const assetsForScope = useMemo(() => assets.filter((a) => {
    if (scopeFilters.client_id !== "all" && a.client_id !== scopeFilters.client_id) return false;
    if (scopeFilters.legal_entity_id !== "all" && a.legal_entity_id !== scopeFilters.legal_entity_id) return false;
    return true;
  }), [assets, scopeFilters.client_id, scopeFilters.legal_entity_id]);

  const scopeFiltered = useMemo(() => certs.filter((c) => {
    if (scopeFilters.asset_id !== "all") {
      if (c.asset_id !== scopeFilters.asset_id) return false;
    } else if (scopeFilters.legal_entity_id !== "all" || scopeFilters.client_id !== "all") {
      const a = c.asset_id ? assetById[c.asset_id] : null;
      if (!a) return false;
      if (scopeFilters.legal_entity_id !== "all" && a.legal_entity_id !== scopeFilters.legal_entity_id) return false;
      if (scopeFilters.client_id !== "all" && a.client_id !== scopeFilters.client_id) return false;
    }
    return true;
  }), [certs, scopeFilters, assetById]);

  const filtered = useMemo(
    () => statusFilter === "all" ? scopeFiltered : scopeFiltered.filter((c) => c.status === statusFilter),
    [scopeFiltered, statusFilter]
  );

  const counts = useMemo(() => {
    const c = { valid: 0, expiring_soon: 0, expired: 0, unknown: 0 };
    scopeFiltered.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [scopeFiltered]);

  const upsert = useMutation({
    mutationFn: (d) => editing ? api.patch(`/insurance-certificates/${editing.id}`, d) : api.post("/insurance-certificates", d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["certificates"] });
      toast.success(editing ? "Attestation mise à jour" : "Attestation créée");
      setOpenDialog(false); setEditing(null); setForm(emptyForm);
    },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/insurance-certificates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["certificates"] }); toast.success("Attestation supprimée"); },
    onError: (e) => toast.error(extractError(e, "Suppression impossible")),
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpenDialog(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c, coverage_amount: c.coverage_amount ?? "", asset_id: c.asset_id || "", policy_id: c.policy_id || "" });
    setOpenDialog(true);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Attach to asset when known (backend requires a target for non-admin); else fallback to asset_id or plain doc.
      const params = new URLSearchParams();
      if (form.asset_id) params.set("asset_id", form.asset_id);
      const { data: doc } = await api.post(`/upload${params.toString() ? `?${params}` : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, storage_path: doc.url }));
      toast.success("Pièce téléversée");
    } catch (e) {
      toast.error(extractError(e, "Upload impossible — attache d'abord un actif si tu n'es pas admin"));
    } finally {
      setUploadingFile(false);
    }
  };

  const submit = () => {
    const payload = {};
    for (const k of Object.keys(emptyForm)) {
      const v = form[k];
      if (v === "" || v === null || v === undefined) continue;
      if (k === "coverage_amount") payload[k] = Number(v);
      else payload[k] = v;
    }
    if (!payload.holder_name || !payload.insurer_name || !payload.start_date || !payload.end_date) {
      toast.error("Renseigne : nom du souscripteur, assureur, dates de début et de fin");
      return;
    }
    if (!editing) {
      // asset_id/contractor_id/policy_id might be empty string — omit
      if (!payload.asset_id) delete payload.asset_id;
      if (!payload.contractor_id) delete payload.contractor_id;
      if (!payload.policy_id) delete payload.policy_id;
    }
    upsert.mutate(payload);
  };

  const downloadComplianceReport = async () => {
    try {
      toast.loading("Génération du rapport de conformité…");
      const res = await api.get("/insurance-certificates/compliance-report.pdf", { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `rapport_conformite_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.dismiss();
      toast.success("Rapport de conformité téléchargé");
    } catch (e) {
      toast.dismiss();
      toast.error(extractError(e, "Échec de la génération du rapport"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Attestations d&apos;assurance</h1>
          <p className="text-sm text-slate-500 mt-1.5">{scopeFiltered.length} attestation(s) suivies</p>
          <div className="mt-1.5"><ContextualHelpLink page="insurance" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="compliance-report-btn" variant="outline" onClick={downloadComplianceReport}><Download size={16} className="mr-1.5" /> Rapport conformité</Button>
          <Button data-testid="new-cert-btn" onClick={openCreate} className="text-white" style={{ background: "#023E73" }}><Plus size={16} className="mr-1.5" /> Nouvelle attestation</Button>
        </div>
      </div>

      {/* Scope filters: Client -> Legal entity -> Site */}
      <Card className="p-3 border-slate-200">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={scopeFilters.client_id} onValueChange={(v) => setScopeFilters({ ...scopeFilters, client_id: v, legal_entity_id: "all", asset_id: "all" })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scopeFilters.legal_entity_id} onValueChange={(v) => setScopeFilters({ ...scopeFilters, legal_entity_id: v, asset_id: "all" })}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="filter-legal-entity"><SelectValue placeholder="Entité juridique" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes entités</SelectItem>
              {legalEntitiesForClient.map((le) => <SelectItem key={le.id} value={le.id}>{le.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scopeFilters.asset_id} onValueChange={(v) => setScopeFilters({ ...scopeFilters, asset_id: v })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-asset"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous sites</SelectItem>
              {assetsForScope.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {(scopeFilters.client_id !== "all" || scopeFilters.legal_entity_id !== "all" || scopeFilters.asset_id !== "all") && (
            <Button
              data-testid="reset-filters"
              variant="ghost" size="sm" className="h-9 text-xs text-slate-600"
              onClick={() => setScopeFilters({ client_id: "all", legal_entity_id: "all", asset_id: "all" })}
            >Réinitialiser</Button>
          )}
          <div className="text-xs text-slate-500 ml-auto">
            {scopeFilters.client_id !== "all" || scopeFilters.legal_entity_id !== "all" || scopeFilters.asset_id !== "all"
              ? "Portée restreinte — les attestations sans actif rattaché sont masquées."
              : ""}
          </div>
        </div>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["all", "valid", "expiring_soon", "expired"].map((k) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setStatusFilter(k)} className={`text-left p-4 rounded-lg border transition ${statusFilter === k ? "border-[#023E73] shadow-md" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k === "all" ? "Toutes" : STATUS[k]?.label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{k === "all" ? scopeFiltered.length : counts[k] || 0}</div>
          </button>
        ))}
      </div>

      {/* List */}
      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Souscripteur</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Site</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Réf. interne site</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Type</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Assureur</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">N° police</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Police liée</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Couverture</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Validité</th>
              <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Statut</th>
              <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const st = STATUS[c.status] || STATUS.unknown;
              const asset = c.asset_id ? assetById[c.asset_id] : null;
              return (
                <tr key={c.id} data-testid={`cert-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50/40">
                  <td className="px-4 py-2 font-semibold">{c.holder_name}</td>
                  <td className="px-4 py-2 text-xs" data-testid={`cert-asset-name-${c.id}`}>{asset?.name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-500" data-testid={`cert-asset-ref-${c.id}`}>{asset?.internal_ref || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 text-xs">{pl.labelOf("certificate.holder_type", c.holder_type, HOLDER_TYPES)}</td>
                  <td className="px-4 py-2 text-xs">{c.insurer_name}</td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-500">{c.policy_number || "—"}</td>
                  <td className="px-4 py-2 text-xs" data-testid={`cert-policy-link-${c.id}`}>{policyById[c.policy_id]?.policy_number || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 text-xs">{pl.labelOf("certificate.coverage_type", c.coverage_type, COVERAGE_TYPES)}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{c.start_date?.slice(0,10)} → {c.end_date?.slice(0,10)}</td>
                  <td className="px-4 py-2"><span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {c.storage_path && (
                      <Button data-testid={`view-${c.id}`} variant="ghost" size="sm" onClick={() => setViewing(c)} title="Voir le PDF"><Eye size={14} className="text-[#023E73]" /></Button>
                    )}
                    <Button data-testid={`edit-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil size={14} /></Button>
                    <Button data-testid={`delete-${c.id}`} variant="ghost" size="sm" onClick={() => { if (window.confirm(`Supprimer l'attestation de ${c.holder_name} ?`)) del.mutate(c.id); }}><Trash2 size={14} className="text-red-500" /></Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400 text-sm">
                <ShieldCheck size={32} className="mx-auto mb-2 text-slate-300" />
                {certs.length === 0 ? "Aucune attestation. Ajoute la première pour suivre la couverture d'assurance de tes intervenants." : "Aucune attestation ne correspond à ce filtre."}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Upsert dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier l'attestation" : "Nouvelle attestation d'assurance"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Souscripteur *</Label><Input data-testid="cert-holder" value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} /></div>
              <div>
                <Label>Type de souscripteur</Label>
                <Select value={form.holder_type} onValueChange={(v) => setForm({ ...form, holder_type: v })}>
                  <SelectTrigger data-testid="cert-holder-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pl.options("certificate.holder_type", HOLDER_TYPES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assureur *</Label><Input data-testid="cert-insurer" value={form.insurer_name} onChange={(e) => setForm({ ...form, insurer_name: e.target.value })} /></div>
              <div><Label>N° de police</Label><Input data-testid="cert-policy" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type de couverture</Label>
                <Select value={form.coverage_type} onValueChange={(v) => setForm({ ...form, coverage_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pl.options("certificate.coverage_type", COVERAGE_TYPES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Montant garanti (€)</Label><Input data-testid="cert-amount" type="number" step="1" value={form.coverage_amount} onChange={(e) => setForm({ ...form, coverage_amount: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date de début *</Label><Input data-testid="cert-start" type="date" value={form.start_date?.slice(0,10) || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Date de fin *</Label><Input data-testid="cert-end" type="date" value={form.end_date?.slice(0,10) || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div>
              <Label>Actif rattaché (optionnel)</Label>
              <Select value={form.asset_id || "none"} onValueChange={(v) => setForm({ ...form, asset_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cert-asset"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun actif</SelectItem>
                  {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Police d&apos;assurance liée (optionnel)</Label>
              <Select value={form.policy_id || "none"} onValueChange={(v) => setForm({ ...form, policy_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cert-policy-link"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune police</SelectItem>
                  {policiesForAsset.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.policy_number} — {p.insurer} ({assetById[p.asset_id]?.name || "?"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Rattache l&apos;attestation à une police pour tracer les renouvellements et déclencher les rappels J-30 / J-7 avant expiration.</p>
            </div>
            <div>
              <Label>Pièce jointe (attestation PDF ou image)</Label>
              <div className="mt-1 flex items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold text-[#023E73] border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">
                  <input data-testid="cert-file-input" type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
                  <Upload size={12} /> {uploadingFile ? "Envoi…" : (form.storage_path ? "Remplacer" : "Téléverser")}
                </label>
                {form.storage_path && (
                  <div className="text-xs text-emerald-700 inline-flex items-center gap-1"><FileText size={12} /> Fichier attaché</div>
                )}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            {editing && <CertificateAttachments certificateId={editing.id} />}
          </div>
          <DialogFooter>
            <Button data-testid="cert-submit" onClick={submit} disabled={upsert.isPending} className="text-white" style={{ background: "#023E73" }}>
              {upsert.isPending ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#023E73]" />
              {viewing?.holder_name} — {viewing?.insurer_name}
            </DialogTitle>
          </DialogHeader>
          {viewing?.storage_path && <PdfViewer src={viewing.storage_path} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CertificateAttachments({ certificateId }) {
  const qc = useQueryClient();
  const { data: docs = [] } = useQuery({
    queryKey: ["docs", "certificate", certificateId],
    queryFn: () => api.get(`/documents?certificate_id=${certificateId}`).then((r) => r.data),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["docs", "certificate", certificateId] });
  const removeDoc = async (docId) => {
    await api.delete(`/documents/${docId}`);
    invalidate();
    toast.success("Pièce jointe supprimée");
  };
  return (
    <div className="border-t border-slate-100 pt-3">
      <Label>Pièces jointes supplémentaires (CGV, annexes…)</Label>
      <div className="mt-1">
        <FileUploader certificateId={certificateId} label="Ajouter une pièce jointe" onUploaded={invalidate} />
      </div>
      {docs.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {docs.map((d) => (
            <div key={d.id} data-testid={`cert-attachment-${d.id}`} className="relative aspect-square rounded-md border border-slate-200 overflow-hidden bg-slate-50 group">
              {d.mime_type?.startsWith("image") ? (
                <AuthImage src={d.url} alt={d.name} className="w-full h-full object-cover" fallback={<div className="p-2 text-[10px] text-slate-400">{d.name}</div>} />
              ) : (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="p-2 flex flex-col items-center justify-center h-full text-slate-500">
                  <FileText size={20} /><div className="text-[10px] mt-1 text-center line-clamp-2">{d.name}</div>
                </a>
              )}
              <button
                data-testid={`delete-cert-attachment-${d.id}`}
                onClick={() => removeDoc(d.id)}
                className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition shadow"
              ><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
